import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
    useGetAdminMarathonsQuery,
    useGetAwardableMarathonsQuery,
    usePreviewMarathonPrizeMutation,
    useAwardMarathonPrizeMutation,
} from "../api/marathonAdmin";
import type {
    AdminMarathonAwardPreview,
    AdminMarathonFilters,
} from "../types/marathonAdmin";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";

const typeOptions = [
    { value: "all", labelKey: "marathon:types.all" },
    { value: "1", labelKey: "marathon:types.type_1" },
    { value: "2", labelKey: "marathon:types.type_2" },
    { value: "3", labelKey: "marathon:types.type_3" },
];

const statusOptions = [
    { value: "all", labelKey: "marathon:statuses.all" },
    { value: "0", labelKey: "marathon:statuses.open" },
    { value: "1", labelKey: "marathon:statuses.active" },
    { value: "2", labelKey: "marathon:statuses.finished" },
];

const awardBlockingReasonLabels: Record<string, string> = {
    already_awarded: "уже начислено",
    participant_not_found: "аккаунт участника не найден",
};

const snapshotSourceLabels: Record<string, string> = {
    backup: "исторический бэкап",
    system: "текущий снимок",
    cleanup_guard: "снимок очистки",
    manual_command: "ручной снимок",
};

const MarathonsPage: React.FC = () => {
    const { t } = useTranslation(["marathon", "common"]);
    const { showError, showSuccess, notification, hideNotification } =
        useNotification();
    const [filters, setFilters] = useState({
        type: "all",
        status: "all",
        search: "",
    });
    const [pagination, setPagination] = useState({ page: 1, perPage: 20 });

    // Состояние формы начисления призовых
    const [prizeForm, setPrizeForm] = useState({
        marathonId: "",
        snapshotId: "",
        ticketNumber: "",
    });
    const [awardPreview, setAwardPreview] =
        useState<AdminMarathonAwardPreview | null>(null);

    const queryFilters: AdminMarathonFilters = {
        page: pagination.page,
        per_page: pagination.perPage,
        type: filters.type !== "all" ? Number(filters.type) : undefined,
        status: filters.status !== "all" ? Number(filters.status) : undefined,
        search: filters.search.trim() || undefined,
    };

    const { data, isLoading, isFetching } =
        useGetAdminMarathonsQuery(queryFilters);

    const marathons = useMemo(() => data?.data ?? [], [data?.data]);
    const meta = data?.meta;

    const {
        data: awardableMarathonsData,
        isError: isAwardableMarathonsError,
        isLoading: isAwardableMarathonsLoading,
    } = useGetAwardableMarathonsQuery();
    const awardableMarathons = awardableMarathonsData?.data ?? [];

    // Мутация для начисления призовых
    const [previewPrize, { isLoading: isPreviewingPrize }] =
        usePreviewMarathonPrizeMutation();
    const [awardPrize, { isLoading: isAwardingPrize }] =
        useAwardMarathonPrizeMutation();

    const summaryCards = useMemo(() => {
        const totalParticipants = marathons.reduce(
            (sum, marathon) => sum + (marathon.participants_total ?? 0),
            0
        );
        const totalPaid = marathons.reduce(
            (sum, marathon) => sum + (marathon.participants_paid ?? 0),
            0
        );
        const totalQueue = marathons.reduce(
            (sum, marathon) => sum + (marathon.queue_count ?? 0),
            0
        );

        return [
            {
                label: t("marathon:metrics.participants"),
                value: totalParticipants,
            },
            {
                label: t("marathon:metrics.paid"),
                value: totalPaid,
            },
            {
                label: t("marathon:metrics.pending"),
                value: totalQueue,
            },
        ];
    }, [marathons, t]);

    const handleFilterChange = (
        key: keyof typeof filters,
        value: string
    ): void => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const handleResetFilters = () => {
        setFilters({ type: "all", status: "all", search: "" });
        setPagination({ page: 1, perPage: 20 });
    };

    const handlePageChange = (direction: "prev" | "next") => {
        if (!meta) {
            return;
        }

        if (direction === "prev" && pagination.page > 1) {
            setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
        }

        if (
            direction === "next" &&
            pagination.page < (meta?.last_page ?? pagination.page)
        ) {
            setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
        }
    };

    const buildAwardPayload = () => ({
        marafone_id: Number(prizeForm.marathonId),
        ticket_number: Number(prizeForm.ticketNumber),
        snapshot_id: prizeForm.snapshotId ? Number(prizeForm.snapshotId) : null,
    });

    const handlePreviewPrize = async () => {
        if (!prizeForm.marathonId || !prizeForm.ticketNumber) {
            showError("Выберите марафон и номер билета");
            return;
        }

        try {
            const result = await previewPrize(buildAwardPayload()).unwrap();
            setAwardPreview(result.data);
        } catch (error: any) {
            setAwardPreview(null);
            const errorMessage =
                error?.data?.message || error?.message || "Ошибка при проверке победителя";
            showError(errorMessage);
        }
    };

    const handleAwardPrize = async () => {
        if (!prizeForm.marathonId || !prizeForm.ticketNumber) {
            showError("Заполните все поля");
            return;
        }

        try {
            const result = await awardPrize(buildAwardPayload()).unwrap();

            if (result.success) {
                showSuccess(result.message || "Призовые успешно начислены");
                setPrizeForm({
                    marathonId: "",
                    snapshotId: "",
                    ticketNumber: "",
                });
                setAwardPreview(null);
            }
        } catch (error: any) {
            const errorMessage =
                error?.data?.message || error?.message || "Ошибка при начислении призовых";
            showError(errorMessage);
        }
    };

    const handleAwardableMarathonChange = (value: string) => {
        const [marathonId, snapshotId = ""] = value.split(":");
        setPrizeForm((prev) => ({
            ...prev,
            marathonId,
            snapshotId,
        }));
        setAwardPreview(null);
    };

    const formatAwardableMarathonOption = (
        marathon: (typeof awardableMarathons)[number]
    ) => {
        const sourceLabel = marathon.snapshot_id
            ? snapshotSourceLabels[marathon.snapshot_source ?? ""] ?? "снимок"
            : "live";
        const snapshotLabel = marathon.snapshot_id
            ? `#${marathon.snapshot_id} ${sourceLabel}`
            : sourceLabel;
        const dateLabel = marathon.draw_date ?? marathon.status_label;
        const awardStatusLabel = marathon.award_status
            ? ` - ${marathon.award_status}`
            : "";

        return `ID: ${marathon.marathon_id} - ${marathon.type_label} - ${snapshotLabel} (${dateLabel})${awardStatusLabel}`;
    };

    return (
        <>
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
            <div className="px-4 py-6 space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {t("marathon:admin_title")}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                    {t("marathon:admin_subtitle")}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {summaryCards.map((card) => (
                    <div
                        key={card.label}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
                    >
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {card.label}
                        </p>
                        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                            {card.value}
                        </p>
                    </div>
                ))}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t("marathon:filters.type")}
                        </label>
                        <select
                            value={filters.type}
                            onChange={(e) =>
                                handleFilterChange("type", e.target.value)
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        >
                            {typeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {t(option.labelKey, { ns: "marathon" })}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t("marathon:filters.status")}
                        </label>
                        <select
                            value={filters.status}
                            onChange={(e) =>
                                handleFilterChange("status", e.target.value)
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        >
                            {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {t(option.labelKey, { ns: "marathon" })}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t("marathon:filters.search")}
                        </label>
                        <input
                            type="text"
                            value={filters.search}
                            onChange={(e) =>
                                handleFilterChange("search", e.target.value)
                            }
                            placeholder={t("common:search")}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleResetFilters}
                        className="px-4 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        {t("marathon:filters.reset")}
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                {isLoading ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        {t("common:loading")}
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.id")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.type")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.status")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.participants")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.tickets")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.bets")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.actions")}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {marathons.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                                    >
                                        {t("marathon:no_data")}
                                    </td>
                                </tr>
                            ) : (
                                marathons.map((marathon) => (
                                    <tr key={marathon.id}>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.id}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.type_label}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                                {marathon.status_label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.participants_paid}/
                                            {marathon.participants_total}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.tickets_active}/
                                            {marathon.tickets_total}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.bets_total}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <Link
                                                to={`/dashboard/marathons/${marathon.id}`}
                                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                            >
                                                {t("marathon:view_details")}
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}

                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                        {meta &&
                            t("marathon:pagination.page", {
                                page: meta.current_page,
                                total: meta.last_page,
                            })}
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => handlePageChange("prev")}
                            disabled={pagination.page <= 1 || isFetching}
                            className="px-3 py-1 text-sm border rounded disabled:opacity-50 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                        >
                            {t("common:previous")}
                        </button>
                        <button
                            type="button"
                            onClick={() => handlePageChange("next")}
                            disabled={
                                !meta ||
                                pagination.page >= meta.last_page ||
                                isFetching
                            }
                            className="px-3 py-1 text-sm border rounded disabled:opacity-50 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                        >
                            {t("common:next")}
                        </button>
                    </div>
                </div>
            </div>

            {/* Форма начисления призовых */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-200">
                    Начислить призовые победителям
                </h2>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Марафон
                        </label>
                        <select
                            value={
                                prizeForm.marathonId
                                    ? `${prizeForm.marathonId}:${prizeForm.snapshotId}`
                                    : ""
                            }
                            onChange={(e) => handleAwardableMarathonChange(e.target.value)}
                            disabled={
                                isAwardableMarathonsLoading ||
                                isAwardableMarathonsError
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        >
                            <option value="">
                                {isAwardableMarathonsLoading
                                    ? "Загрузка марафонов..."
                                    : isAwardableMarathonsError
                                      ? "Не удалось загрузить марафоны"
                                      : "Выберите марафон"}
                            </option>
                            {awardableMarathons.map((marathon) => (
                                <option
                                    key={`${marathon.marathon_id}:${marathon.snapshot_id ?? "live"}`}
                                    value={`${marathon.marathon_id}:${marathon.snapshot_id ?? ""}`}
                                >
                                    {formatAwardableMarathonOption(marathon)}
                                </option>
                            ))}
                        </select>
                        {isAwardableMarathonsError && (
                            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                Проверьте права администратора, API key и IP whitelist.
                            </p>
                        )}
                        {!isAwardableMarathonsLoading &&
                            !isAwardableMarathonsError &&
                            awardableMarathons.length === 0 && (
                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    Нет марафонов, доступных для начисления.
                                </p>
                            )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Номер билета
                        </label>
                        <input
                            type="number"
                            value={prizeForm.ticketNumber}
                            onChange={(e) =>
                                {
                                    setPrizeForm((prev) => ({
                                        ...prev,
                                        ticketNumber: e.target.value,
                                    }));
                                    setAwardPreview(null);
                                }
                            }
                            placeholder="Введите номер билета"
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    {awardPreview && (
                        <div className="rounded border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-700 dark:text-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <span className="block text-gray-500 dark:text-gray-400">
                                        Победитель
                                    </span>
                                    <span>
                                        ID: {awardPreview.winner_user.id}
                                        {awardPreview.winner_user.name
                                            ? ` - ${awardPreview.winner_user.name}`
                                            : ""}
                                    </span>
                                </div>
                                <div>
                                    <span className="block text-gray-500 dark:text-gray-400">
                                        Билет
                                    </span>
                                    <span>
                                        #{awardPreview.ticket_id}, номер {awardPreview.winning_draw_number}
                                    </span>
                                </div>
                                <div>
                                    <span className="block text-gray-500 dark:text-gray-400">
                                        Статус
                                    </span>
                                    <span>
                                        {awardPreview.award_status ?? "не начислен"}
                                        {awardPreview.blocking_reason
                                            ? ` (${awardBlockingReasonLabels[awardPreview.blocking_reason] ?? awardPreview.blocking_reason})`
                                            : ""}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handlePreviewPrize}
                            disabled={
                                isPreviewingPrize ||
                                !prizeForm.marathonId ||
                                !prizeForm.ticketNumber
                            }
                            className="px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isPreviewingPrize ? "Проверка..." : "Проверить победителя"}
                        </button>
                        <button
                            type="button"
                            onClick={handleAwardPrize}
                            disabled={
                                isAwardingPrize ||
                                !prizeForm.marathonId ||
                                !prizeForm.ticketNumber ||
                                !awardPreview ||
                                !awardPreview.can_award
                            }
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isAwardingPrize ? "Начисление..." : "Начислить призовые"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default MarathonsPage;
